import { AlertCircle } from "lucide-react"

import { ErrorApi } from "@/api/cliente"
import {
  type Borrador,
  type Cargo,
  type Departamento,
  type Descriptivo,
  NIVELES,
  type NivelCargo,
} from "@/componentes/cargos/cargo"
import { FuncionesDelFlujo } from "@/componentes/cargos/FuncionesDelFlujo"
import { FuncionesEditables } from "@/componentes/cargos/FuncionesEditables"
import { HojaDeRuta } from "@/componentes/cargos/HojaDeRuta"
import { LecturaDeCargo } from "@/componentes/cargos/LecturaDeCargo"

/**
 * Un puesto: cómo se llama, en qué área está y qué se espera de él.
 *
 * ⚠ **El área se elige en el CARGO, no en el departamento.** Es el cargo el que dice a
 * qué área pertenece; el área no guarda la lista. Con la lista en los dos sitios, mover
 * un puesto obligaría a tocarlo en ambos, y uno se quedaría viejo.
 *
 * ⚠ **Las funciones son la semilla del contrato**, y por eso su ORDEN importa: es el
 * que va a salir impreso. Se reordenan con botones y no arrastrando — WCAG 2.5.7
 * exige una alternativa de un solo puntero, y construida después no llega nunca.
 *
 * ⚠ **Un cargo no se borra: se retira.** Lo que tuvo gente es lo que explica qué
 * hacía esa gente, y borrarlo dejaría contratos hablando de un puesto que ya no
 * existe. Con gente dentro ni siquiera se retira, y se dice ANTES de pulsar en vez
 * de enseñar un 409 después.
 *
 * ⚠ **Sin permiso para editar se pinta como texto**, no como un formulario
 * deshabilitado: un campo gris invita a buscar por qué no deja escribir.
 */
export function DescriptivoDeCargo({
  cargo,
  descriptivo,
  borrador,
  setBorrador,
  departamentos,
  cargos,
  soloLectura,
  sucio,
  guardando,
  error,
  onGuardar,
  onRetirar,
}: {
  cargo: Cargo
  descriptivo?: Descriptivo
  borrador: Borrador
  setBorrador: (b: Borrador) => void
  departamentos?: Departamento[]
  /** El catálogo, para elegir a quién reporta. */
  cargos?: Cargo[]
  soloLectura: boolean
  sucio: boolean
  guardando: boolean
  error: unknown
  onGuardar: () => void
  onRetirar: (activo: boolean) => void
}) {
  if (soloLectura) return <LecturaDeCargo cargo={cargo} descriptivo={descriptivo} />

  const poner = (parcial: Partial<Borrador>) => setBorrador({ ...borrador, ...parcial })
  const funciones = borrador.funciones

  // Las archivadas no se ofrecen, salvo la que ya tiene: si no, su propia área
  // desaparecería del selector y guardar cualquier otro cambio se la quitaría.
  const suya = borrador.departamento_id
  const areas = (departamentos ?? []).filter((d) => d.activo || String(d.id) === suya)
  // Mientras la lista llega, la suya se enseña igual: con el selector vacío parecería
  // que no tiene ninguna.
  const faltaLaSuya = suya !== "" && !areas.some((d) => String(d.id) === suya)
  // A quién puede reportar: cualquier otro cargo activo, más el que ya tiene. Los
  // ciclos los rechaza el servidor con su mensaje.
  const jefe = borrador.reporta_a_cargo_id
  const jefes = (cargos ?? []).filter(
    (c) => c.id !== cargo.id && (c.activo || String(c.id) === jefe),
  )

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="rotulo flex-1">Descriptivo de «{cargo.nombre}»</h2>
        {/* Con gente dentro no se retira, y se dice ANTES de pulsar: enseñar un
            botón que va a responder 409 es ofrecer algo que no se puede hacer. */}
        {cargo.activo && cargo.personas === 0 && (
          <button
            type="button"
            onClick={() => onRetirar(false)}
            className="boton-secundario h-8 px-2 text-xs"
          >
            Retirar del catálogo
          </button>
        )}
        {!cargo.activo && (
          <button
            type="button"
            onClick={() => onRetirar(true)}
            className="boton-secundario h-8 px-2 text-xs"
          >
            Volver a ofrecerlo
          </button>
        )}
        <button
          type="button"
          disabled={!sucio || guardando || borrador.nombre.trim().length < 2}
          onClick={onGuardar}
          data-testid="guardar-cargo"
          className="boton-primario h-8 px-3 text-xs"
        >
          {guardando ? "Guardando…" : "Guardar cargo"}
        </button>
      </div>

      {cargo.activo && cargo.personas > 0 && (
        <p className="mb-3 text-xs" style={{ color: "var(--fg-muted)" }}>
          Lo tienen {cargo.personas} persona{cargo.personas === 1 ? "" : "s"}: para retirarlo,
          cámbiaselo antes.
        </p>
      )}

      {error !== null && error !== undefined && (
        <p
          role="alert"
          className="mb-3 flex items-start gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {error instanceof ErrorApi ? error.message : "No se pudo guardar"}
        </p>
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="cargo-puesto" className="mb-1 block text-xs font-medium uppercase">
            Nombre del puesto
          </label>
          <input
            id="cargo-puesto"
            required
            minLength={2}
            maxLength={120}
            className="campo"
            value={borrador.nombre}
            onChange={(e) => poner({ nombre: e.target.value })}
          />
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            Es el mismo en todas las fichas, contratos y organigramas: cambiarlo aquí los cambia a
            todos.
          </p>
        </div>
        <div>
          <label htmlFor="cargo-area" className="mb-1 block text-xs font-medium uppercase">
            Departamento
          </label>
          <select
            id="cargo-area"
            className="campo"
            value={suya}
            onChange={(e) => poner({ departamento_id: e.target.value })}
          >
            <option value="">Sin departamento</option>
            {faltaLaSuya && (
              <option value={suya}>{cargo.departamento_nombre ?? "Cargando…"}</option>
            )}
            {areas.map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.nombre}
                {d.activo ? "" : " (archivado)"}
              </option>
            ))}
          </select>
        </div>
        {suya !== "" && (
          <fieldset className="sm:col-span-2">
            <legend className="mb-1 block text-xs font-medium uppercase">
              También en (excepción)
            </legend>
            <p className="mb-1 text-xs" style={{ color: "var(--fg-muted)" }}>
              Sólo si el mismo puesto existe en otro departamento —«Ejecutivo de Cuentas» en
              Operaciones y en Finanzas—. Cada persona elige el suyo en su ficha.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {areas
                .filter((d) => String(d.id) !== suya && d.activo)
                .map((d) => (
                  <label key={d.id} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={borrador.departamentos_adicionales.includes(d.id)}
                      onChange={(e) =>
                        poner({
                          departamentos_adicionales: (e.target.checked
                            ? [...borrador.departamentos_adicionales, d.id]
                            : borrador.departamentos_adicionales.filter((x) => x !== d.id)
                          ).sort((a, b) => a - b),
                        })
                      }
                    />
                    {d.nombre}
                  </label>
                ))}
            </div>
          </fieldset>
        )}
        <div>
          <label htmlFor="cargo-nivel" className="mb-1 block text-xs font-medium uppercase">
            Nivel en la estructura
          </label>
          <select
            id="cargo-nivel"
            className="campo"
            value={borrador.nivel}
            onChange={(e) => poner({ nivel: e.target.value as NivelCargo })}
          >
            {NIVELES.map((n) => (
              <option key={n.valor} value={n.valor}>
                {n.etiqueta}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            Decide el color de su caja en el organigrama.
          </p>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="cargo-jefe" className="mb-1 block text-xs font-medium uppercase">
            Reporta a
          </label>
          <select
            id="cargo-jefe"
            className="campo"
            value={borrador.reporta_a_cargo_id}
            onChange={(e) => poner({ reporta_a_cargo_id: e.target.value })}
          >
            <option value="">A nadie (dirección)</option>
            {jefes.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.nombre}
                {c.activo ? "" : " (retirado)"}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            De aquí sale el organigrama propuesto («Completar desde cargos»).
            {cargo.le_reportan > 0 &&
              ` Le reporta${cargo.le_reportan === 1 ? "" : "n"} ${cargo.le_reportan} cargo${cargo.le_reportan === 1 ? "" : "s"}.`}
          </p>
        </div>
      </div>

      <label htmlFor="cargo-descripcion" className="mb-1 block text-xs font-medium uppercase">
        Para qué existe el puesto
      </label>
      <textarea
        id="cargo-descripcion"
        rows={3}
        className="campo mb-4"
        value={borrador.descripcion}
        onChange={(e) => poner({ descripcion: e.target.value })}
        placeholder="El párrafo que encabeza el contrato, antes de la lista de funciones."
      />

      <FuncionesEditables
        funciones={funciones}
        onCambiar={(nuevas) => poner({ funciones: nuevas })}
      />

      <HojaDeRuta
        actividades={borrador.hoja_de_ruta}
        onCambiar={(nuevas) => poner({ hoja_de_ruta: nuevas })}
      />

      <FuncionesDelFlujo descriptivo={descriptivo} />
    </div>
  )
}
